<script setup lang="ts">
import { computed, ref, watch, useTemplateRef } from 'vue';
import { useScroll } from '@vueuse/core';

import {
  useStatsigEventsStore,
  useBrowserStore,
  useCustomerFlowStore,
  useRouterStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type { AppMaskType } from '@libs/ui';
import { AppButton, AppMask } from '@libs/ui';

import type { HomeFlightData } from '../../../../modules/airportSelector';
import { CarTripPlannerButton } from '../../../../modules/carTripPlannerButton';
import { HomeAirportSelectorButton } from '../../../../modules/airportSelector';
import { PaxSelectorButton } from '../../../../modules/paxSelector';
import { useTripSummaryStore } from '../../stores';

interface TripOverviewHeaderActionsProps {
  paxesButtonText: string;
  homeFlightData: HomeFlightData | null;
  tripStartDate: Date | undefined;
  numberOfDestinations: number;
  priceCalendarButtonText: string;
  numberOfNights: number;
  firstDestinationName?: string;
  isUserSelectedItinerary?: boolean;
}

const props = defineProps<TripOverviewHeaderActionsProps>();

interface TripOverviewHeaderActionsEmits {
  (eventName: 'pax-click'): void;
  (eventName: 'edit-trip'): void;
  (eventName: 'home-airport-click'): void;
  (eventName: 'months-selector-click'): void;
  (eventName: 'navigate-to-car-trip'): void;
}

const emit = defineEmits<TripOverviewHeaderActionsEmits>();

const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();
const customerFlowStore = useCustomerFlowStore();
const routerStore = useRouterStore();
const tripSummaryStore = useTripSummaryStore();

const actionsRef = useTemplateRef('actionsRef');
const { t } = i18n.global;

const { arrivedState } = useScroll(actionsRef, {
  behavior: 'smooth',
});

const maskType = computed<AppMaskType>(() => {
  if (arrivedState.left && arrivedState.right) {
    return 'none';
  }
  if (arrivedState.left) {
    return 'right';
  }
  if (arrivedState.right) {
    return 'left';
  }
  return 'both';
});

const isCarTripPlannerUser = customerFlowStore.isCarTripFlow;

const monthLabel = computed<string>(() =>
  props.isUserSelectedItinerary
    ? props.priceCalendarButtonText
    : `${t('planner.flexible_in')} ${props.priceCalendarButtonText}`
);

const firstDestinationNameSaved = ref<string | undefined>(undefined);

watch(
  () => props.firstDestinationName,
  (newValue) => {
    if (newValue && newValue !== firstDestinationNameSaved.value) {
      firstDestinationNameSaved.value = newValue;
    }
  },
  { immediate: true }
);

const firstDestinationNameDisplay = computed<string>(() => {
  if (props.numberOfDestinations === 1 && firstDestinationNameSaved.value) {
    return firstDestinationNameSaved.value;
  }

  return `${props.numberOfDestinations} ${t(
    'planner.destination',
    props.numberOfDestinations
  ).toLowerCase()}`;
});

function openEditTripModal(actionType: 'editTrip' | 'editStay'): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType,
  });

  emit('edit-trip');
}

function openHomeAirportModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'homeAirport',
  });

  emit('home-airport-click');
}

function openPaxSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'paxSelector',
  });

  emit('pax-click');
}

function openMonthsSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'monthsSelector',
  });

  emit('months-selector-click');
}

async function navegateToPrintMode(): Promise<void> {
  statsigEventsStore.logTripOverviewPrintModeOpened({
    source: 'button',
  });
  await routerStore.navigateToTripOverviewPrint();
}
</script>

<template>
  <AppMask :type="maskType">
    <div ref="actionsRef" class="trip-overview-header-actions">
      <div class="trip-overview-header-actions-wrapper">
        <CarTripPlannerButton v-if="!!isCarTripPlannerUser" />

        <div v-if="!!isCarTripPlannerUser" class="separator" />

        <HomeAirportSelectorButton
          v-if="props.homeFlightData"
          :home-flight-data="props.homeFlightData"
          @click="openHomeAirportModal"
        />

        <AppButton
          class="edit-trip-button"
          type="modern"
          state="enabled"
          size="small"
          :left-icon="{
            size: '18',
            name: 'locationMarkOutlined',
          }"
          :right-icon="{
            size: '18',
            name: 'chevronDown',
          }"
          @click="openEditTripModal('editTrip')"
        >
          <div class="button-content">
            <span>
              {{ firstDestinationNameDisplay }}
            </span>
          </div>
        </AppButton>

        <PaxSelectorButton
          :button-text="props.paxesButtonText"
          @click="openPaxSelectorModal"
        />

        <AppButton
          class="months-selector-modal-button"
          type="modern"
          state="enabled"
          size="small"
          :left-icon="{
            size: '18',
            name: 'calendarOutlined',
          }"
          :right-icon="{
            size: '18',
            name: 'chevronDown',
          }"
          @click="openMonthsSelectorModal()"
        >
          {{ monthLabel }}
        </AppButton>

        <AppButton
          class="edit-trip-button"
          type="modern"
          state="enabled"
          size="small"
          :left-icon="{
            size: '18',
            name: 'moon',
          }"
          :right-icon="{
            size: '18',
            name: 'chevronDown',
          }"
          @click="openEditTripModal('editStay')"
        >
          <div class="button-content">
            <span>
              {{ props.numberOfNights }}
              {{ $t('planner.nights', props.numberOfNights).toLowerCase() }}
            </span>
          </div>
        </AppButton>
      </div>
      <div
        v-if="browserStore.isLargerThan600px"
        class="trip-overview-header-actions-wrapper"
      >
        <AppButton
          v-if="tripSummaryStore.isEveryIngredientAvailable"
          class="print-button"
          type="modern"
          state="enabled"
          size="small"
          :left-icon="{
            size: '18',
            name: 'printer',
          }"
          @click="navegateToPrintMode"
        >
          <div class="button-content">
            <span>{{ $t('planner.print_view') }}</span>
          </div>
        </AppButton>
      </div>
    </div>
  </AppMask>
</template>

<style scoped lang="scss">
.trip-overview-header-actions {
  display: flex;
  overscroll-behavior-x: contain; // NOTE: this prevents going back/forward in the browser navigation when reaching the scroll limit
  overflow-y: hidden;
  align-items: space-between;
  justify-content: space-between;
  gap: var(--spacing-8);
  padding-bottom: var(--spacing-16);

  .trip-overview-header-actions-wrapper {
    display: flex;
    gap: var(--spacing-8);
    // NOTE: To correctly display button focused state
    padding: var(--spacing-2);
    .separator {
      width: 1px;
      background-color: var(--color-scrollbar);
      margin: 0 var(--spacing-8);
    }
    .map-button {
      width: max-content;
    }
  }
}
</style>
